import type { PublicClient } from "viem"
import { Abi, Address } from "viem"

import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { IChunkedRpcStrategy } from "@/domains/library/rpc-strategy"
import { clFarmLens } from "@/domains/abis"
import { LcAddress } from "@/domains/core"

export type ClFarmOnChainTransportAddresses = {
  scalensClFarms: LcAddress
}

export type ClFarmLensInput = [Address, Address, Address]

export const earnedAbi = [
  {
    constant: true,
    inputs: [
      { internalType: "address", name: "account", type: "address" },
      { internalType: "address", name: "_rewardsToken", type: "address" },
    ],
    name: "earned",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
] as const

export class ClFarmOnChainTransport {
  constructor(
    private getPublicClient: () => PublicClient,
    private readonly addresses: ClFarmOnChainTransportAddresses,
    private chunkedRpcStrategy: IChunkedRpcStrategy,
  ) {}

  /**
   * Load CL farm data from on-chain
   * Pure data loading - no state, no caching, no side effects
   */
  async loadClFarmData(
    inputs: Array<ClFarmLensInput>,
    query: IOrchestratedQuerySender<any>,
    defaultChunkSize: number = 25,
  ): Promise<any[]> {
    const publicClient = this.getPublicClient()

    const chunkResults = await this.chunkedRpcStrategy.executeChunked(
      inputs,
      query,
      defaultChunkSize,
      async (chunk: ClFarmLensInput[], chunkIndex: number) => {
        return await publicClient.readContract({
          abi: clFarmLens,
          address: this.addresses.scalensClFarms.toViemAddress(),
          functionName: "getClsets",
          args: [chunk],
        })
      },
      query.getRpcMetricsCollector(),
    )

    return chunkResults.flat()
  }

  /**
   * Load earned rewards from CL farms via multicall
   * Pure data loading - no state, no caching, no side effects
   * Since there are only ~20 farms, we can do this in one multicall
   */
  async loadClFarmEarnedRewards(
    contracts: Array<{
      address: Address
      abi: Abi
      functionName: string
      args: [Address, Address] // [governanceToken, account]
    }>,
  ): Promise<Array<{ status: "success" | "failure"; result?: bigint; error?: Error }>> {
    if (contracts.length === 0) {
      return []
    }

    const publicClient = this.getPublicClient()

    // Use viem multicall via PublicClient
    const results = await publicClient.multicall({
      contracts: contracts.map((contract) => ({
        address: contract.address,
        abi: contract.abi,
        functionName: contract.functionName,
        args: contract.args,
      })),
    })

    return results.map((result) => {
      if (result.status === "success") {
        return {
          status: "success" as const,
          result: result.result as bigint,
          error: undefined,
        }
      } else {
        return {
          status: "failure" as const,
          result: undefined,
          error: result.error,
        }
      }
    })
  }
}
